import React from "react";
import { Asterisk, Coins, Dot, Users, Wallet } from "lucide-react";
import { formatDate, getExpiryLabel } from "../utils/dateUtils";
import { TRADING_TOKEN } from "../configs/config";
import Tooltip from "./Tooltip";

export interface Outcome {
  outcome_id: number;
  name: string;
  display_order: number;
  probability?: number;
}

export interface Market {
  id: number;
  question: string;
  category: string;
  expire: number;
  expireSec: number;
  resolved: boolean;
  outcome: number;
  image_url?: string;
  outcomes_count: number;
  outcomes?: Outcome[];
  participants?: number;
  totalInvested?: number;
  version?: number;
  q_yes?: number;
  q_no?: number;
  b?: number;
  yesOdds?: number;
  noOdds?: number;
  outcome_states?: Array<{
    outcome_id: number;
    q_yes: number;
    q_no: number;
    volume: number;
  }>;
  is_refundable?: boolean;
  fee_bps?: number;
  total_collateral_in?: number;
  myInvested?: number;
  created_at?: number;
}

interface MarketsCardProps {
  market: Market;
  nowSec: number;
  session: any;
  watchlist: number[];
  copiedMarketId: number | null;
  onSelectMarket: (id: number) => void;
  onToggleWatchlist: (marketId: number, event: React.MouseEvent) => void;
  onShare: (
    marketId: number,
    platform: "twitter" | "facebook" | "copy",
    event: React.MouseEvent,
  ) => void;
}

const isMarketIlliquid = (market: Market): boolean => {
  if (market.resolved) return false;
  if (market.is_refundable) return true;
  const totalIn = market.total_collateral_in || 0;
  if (totalIn <= 0) return false;
  if (market.version === 3) {
    if (!market.outcome_states || market.outcome_states.length === 0) {
      return false;
    }
    const withVolume = market.outcome_states.filter(
      (state) => (state.volume || 0) > 0,
    ).length;
    return withVolume <= 1;
  }
  if (market.version === 2) {
    const qYes = market.q_yes || 0;
    const qNo = market.q_no || 0;
    return qYes === 0 || qNo === 0;
  }
  return false;
};

const MarketsCard: React.FC<MarketsCardProps> = ({
  market,
  nowSec,
  session,
  watchlist,
  copiedMarketId,
  onSelectMarket,
  onToggleWatchlist,
  onShare,
}) => {
  const isExpired = market.expireSec > 0 && market.expireSec < nowSec;
  const illiquid = isMarketIlliquid(market);
  const isNew =
    !market.resolved && !isExpired && market.created_at
      ? nowSec - market.created_at < 86400
      : false;

  const statusType = market.resolved
    ? "resolved"
    : isExpired && illiquid
      ? "illiquid"
      : isExpired
        ? "expired"
        : isNew
          ? "new"
          : "live";

  const cardBorderClass = `market-card-border-${statusType}`;
  const viewerAccount =
    session?.auth?.actor != null ? String(session.auth.actor) : "";
  const isBinaryMarket = market.outcomes_count === 2;
  const isBinaryV2 = isBinaryMarket && market.version === 2;
  const isThreeOutcomeV3 =
    market.version === 3 &&
    market.outcomes_count === 3 &&
    (market.outcomes?.length ?? 0) === 3;
  let yesPercent = 50;

  if (isBinaryMarket && market.outcomes && market.outcomes.length === 2) {
    const yesOutcome = market.outcomes.find(
      (outcome) => outcome.outcome_id === 0,
    );
    if (yesOutcome) {
      yesPercent =
        market.version === 3
          ? (yesOutcome.probability ?? 50)
          : (market.yesOdds ?? 50);
    }
  }

  const statusBadge = (
    <span
      className={`market-status market-status-${statusType}`}
      style={{
        opacity:
          statusType === "expired" || statusType === "illiquid" ? 1 : 0.8,
      }}
    >
      {statusType === "live" && (
        <Dot
          className="status-dot status-dot-live"
          size={14}
          strokeWidth={1}
          aria-hidden
        />
      )}

      {statusType === "new" && (
        <Asterisk
          className="status-asterisk-new"
          size={16}
          strokeWidth={1.5}
          aria-hidden
        />
      )}
      {market.resolved
        ? "Resolved"
        : isExpired && illiquid
          ? "Illiquid"
          : isExpired
            ? "Expired"
            : isNew
              ? "New"
              : "Live"}
    </span>
  );

  const cardHeader = (
    <>
      <div className="market-top-row">
        <div className="market-category">{market.category}</div>
        {statusBadge}
      </div>

      <div className="market-title-row">
        {market.image_url && (
          <div className="market-thumbnail">
            <img
              src={market.image_url}
              alt={market.question}
              onError={(event) => {
                (
                  event.target as HTMLImageElement
                ).parentElement!.style.display = "none";
              }}
            />
          </div>
        )}
        <h3 className="market-question" title={market.question}>
          {market.question}
        </h3>
      </div>
    </>
  );

  const outcomesAndBar = (
    <>
      {!market.resolved && market.outcomes && market.outcomes.length > 0 && (
        <div
          className={`market-probabilities ${market.outcomes.length > 2 ? "multi-outcome" : ""}${isThreeOutcomeV3 ? " multi-outcome-three" : ""}`}
        >
          {isThreeOutcomeV3 ? (
            <>
              <div className="multi-outcome-three-top">
                {market.outcomes.slice(0, 2).map((outcome) => {
                  const odds =
                    market.version === 3
                      ? (outcome.probability ??
                        Math.round(100 / market.outcomes_count))
                      : outcome.outcome_id === 0
                        ? market.yesOdds
                        : market.noOdds;
                  return (
                    <div
                      key={outcome.outcome_id}
                      className="probability-option other-option"
                    >
                      <div className="probability-label">{outcome.name}</div>
                      <div className="probability-value">{odds}%</div>
                    </div>
                  );
                })}
              </div>
              <div className="multi-outcome-three-bottom">
                {market.outcomes.slice(2, 3).map((outcome) => {
                  const odds =
                    market.version === 3
                      ? (outcome.probability ??
                        Math.round(100 / market.outcomes_count))
                      : outcome.outcome_id === 0
                        ? market.yesOdds
                        : market.noOdds;
                  return (
                    <div
                      key={outcome.outcome_id}
                      className="probability-option other-option"
                    >
                      <div className="probability-label">{outcome.name}</div>
                      <div className="probability-value">{odds}%</div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {market.outcomes
                .slice(0, market.outcomes.length > 2 ? 3 : 2)
                .map((outcome) => {
                  const optionClass = isBinaryMarket
                    ? outcome.outcome_id === 0
                      ? "yes-option"
                      : "no-option"
                    : "other-option";
                  const odds =
                    market.version === 3
                      ? (outcome.probability ??
                        Math.round(100 / market.outcomes_count))
                      : outcome.outcome_id === 0
                        ? market.yesOdds
                        : market.noOdds;
                  return (
                    <div
                      key={outcome.outcome_id}
                      className={`probability-option ${optionClass}`}
                    >
                      <div className="probability-label">{outcome.name}</div>
                      {!isBinaryMarket && (
                        <div className="probability-value">{odds}%</div>
                      )}
                    </div>
                  );
                })}
              {market.outcomes.length > 3 && (
                <div className="probability-option other-option">
                  <div className="probability-label">
                    +{market.outcomes.length - 3} more
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!market.resolved &&
        isBinaryMarket &&
        market.outcomes &&
        market.outcomes.length === 2 && (
          <div className="market-percentage-bar">
            <div
              className="percentage-bar-fill percentage-bar-yes"
              style={{ width: `${yesPercent}%` }}
            >
              {yesPercent > 10 && <span>{yesPercent}%</span>}
            </div>
            <div
              className="percentage-bar-fill percentage-bar-no"
              style={{ width: `${100 - yesPercent}%` }}
            >
              {100 - yesPercent > 10 && <span>{100 - yesPercent}%</span>}
            </div>
          </div>
        )}

      {market.resolved && market.outcomes && market.outcomes.length > 0 && (
        <div
          className={`market-probabilities resolved ${market.outcomes.length > 2 ? "multi-outcome" : ""}${isThreeOutcomeV3 ? " multi-outcome-three" : ""}`}
        >
          {isThreeOutcomeV3 ? (
            <>
              <div className="multi-outcome-three-top">
                {market.outcomes.slice(0, 2).map((outcome) => {
                  const optionClass = isBinaryMarket
                    ? outcome.outcome_id === 0
                      ? "yes-option"
                      : "no-option"
                    : "other-option";
                  const isWinningOutcome =
                    outcome.outcome_id === market.outcome;
                  return (
                    <div
                      key={outcome.outcome_id}
                      className={`probability-option ${optionClass} ${isWinningOutcome ? "winning" : ""}`}
                    >
                      <div className="probability-label">{outcome.name}</div>
                      {!isBinaryMarket && (
                        <div className="probability-value">
                          {outcome.probability?.toFixed(0)}%
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="multi-outcome-three-bottom">
                {market.outcomes.slice(2, 3).map((outcome) => {
                  const optionClass = isBinaryMarket
                    ? outcome.outcome_id === 0
                      ? "yes-option"
                      : "no-option"
                    : "other-option";
                  const isWinningOutcome =
                    outcome.outcome_id === market.outcome;
                  return (
                    <div
                      key={outcome.outcome_id}
                      className={`probability-option ${optionClass} ${isWinningOutcome ? "winning" : ""}`}
                    >
                      <div className="probability-label">{outcome.name}</div>
                      {!isBinaryMarket && (
                        <div className="probability-value">
                          {outcome.probability?.toFixed(0)}%
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {market.outcomes
                .slice(0, market.outcomes.length > 2 ? 3 : 2)
                .map((outcome) => {
                  const optionClass = isBinaryMarket
                    ? outcome.outcome_id === 0
                      ? "yes-option"
                      : "no-option"
                    : "other-option";
                  const isWinningOutcome =
                    outcome.outcome_id === market.outcome;
                  return (
                    <div
                      key={outcome.outcome_id}
                      className={`probability-option ${optionClass} ${isWinningOutcome ? "winning" : ""}`}
                    >
                      <div className="probability-label">{outcome.name}</div>
                      {!isBinaryMarket && (
                        <div className="probability-value">
                          {outcome.probability?.toFixed(0)}%
                        </div>
                      )}
                    </div>
                  );
                })}
              {market.outcomes.length > 3 && (
                <div className="probability-option other-option">
                  <div className="probability-label">
                    +{market.outcomes.length - 3} more
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {market.resolved &&
        isBinaryMarket &&
        market.outcomes &&
        market.outcomes.length === 2 && (
          <div className="market-percentage-bar">
            <div
              className="percentage-bar-fill percentage-bar-yes"
              style={{ width: `${yesPercent}%` }}
            >
              {yesPercent > 10 && <span>{yesPercent}%</span>}
            </div>
            <div
              className="percentage-bar-fill percentage-bar-no"
              style={{ width: `${100 - yesPercent}%` }}
            >
              {100 - yesPercent > 10 && <span>{100 - yesPercent}%</span>}
            </div>
          </div>
        )}
    </>
  );

  const marketInfoSection = (
    <div className="market-info">
      <div className="market-info-stats">
        <Tooltip
          text="The total number of active traders in this market."
          position="top"
        >
          <div className="market-info-stat market-info-stat-participants">
            <span className="market-info-stat-icon" aria-hidden="true">
              <Users size={16} strokeWidth={2.25} />
            </span>
            <span className="market-participants">
              {market.participants || 0}
            </span>
          </div>
        </Tooltip>
        <span className="market-info-stat-divider" aria-hidden="true" />
        <Tooltip text="Total market capital in this market." position="top">
          <div className="market-info-stat">
            <span className="market-info-stat-icon" aria-hidden="true">
              <Coins size={16} strokeWidth={2.25} />
            </span>
            <span className="market-total-invested">
              <span className="market-amount-value">
                {market.totalInvested || 0}
              </span>
              <span className="market-amount-token">{TRADING_TOKEN}</span>
            </span>
          </div>
        </Tooltip>
        {session && (
          <>
            <span className="market-info-stat-divider" aria-hidden="true" />
            <Tooltip
              text={`${viewerAccount}'s total active investment in this market.`}
              position="top"
            >
              <div
                className="market-info-stat market-info-stat-yours"
                aria-label={`Your stake: ${market.myInvested ?? 0} ${TRADING_TOKEN}`}
              >
                <span className="market-info-stat-icon" aria-hidden="true">
                  <Wallet size={16} strokeWidth={2.25} />
                </span>
                <span className="market-my-stake">
                  <span className="market-amount-value">
                    {market.myInvested ?? 0}
                  </span>
                  <span className="market-amount-token">{TRADING_TOKEN}</span>
                </span>
              </div>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`market-card ${cardBorderClass}${isExpired && !market.resolved ? " market-card-expired" : ""}${market.resolved ? " market-card-resolved" : ""}${isBinaryV2 ? " market-card-binary-v2" : ""}`}
      onClick={() => onSelectMarket(market.id)}
    >
      <div className="market-card-content">
        <div className="market-text-content">
          {isBinaryV2 ? (
            <>
              <div className="market-card-binary-v2-top">{cardHeader}</div>
              <div className="market-card-binary-v2-mid">{outcomesAndBar}</div>
              {marketInfoSection}
            </>
          ) : (
            <>
              {cardHeader}
              {outcomesAndBar}
              {marketInfoSection}
            </>
          )}
        </div>
      </div>

      {market.resolved && market.outcomes && (
        <div className="market-outcome">
          Outcome:{" "}
          {market.outcomes.find(
            (outcome) => outcome.outcome_id === market.outcome,
          )?.name || "Unknown"}
        </div>
      )}

      <div className="market-card-footer">
        <div className="market-footer-left">
          <div className="market-share-buttons">
            <button
              className={`share-button watchlist-button ${watchlist.includes(market.id) ? "active" : ""}`}
              onClick={(event) => onToggleWatchlist(market.id, event)}
              title={
                watchlist.includes(market.id)
                  ? "Remove from watchlist"
                  : "Add to watchlist"
              }
              aria-label={
                watchlist.includes(market.id)
                  ? "Remove from watchlist"
                  : "Add to watchlist"
              }
            >
              {watchlist.includes(market.id) ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              )}
            </button>
            <button
              className="share-button share-twitter"
              onClick={(event) => onShare(market.id, "twitter", event)}
              title="Share on X/Twitter"
              aria-label="Share on X/Twitter"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </button>
            <button
              className="share-button share-facebook"
              onClick={(event) => onShare(market.id, "facebook", event)}
              title="Share on Facebook"
              aria-label="Share on Facebook"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </button>
            <button
              className={`share-button share-copy ${copiedMarketId === market.id ? "copied" : ""}`}
              onClick={(event) => onShare(market.id, "copy", event)}
              title={copiedMarketId === market.id ? "Copied!" : "Copy Link"}
              aria-label="Copy Link"
            >
              {copiedMarketId === market.id ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        <div className="market-footer-right">
          <div className="market-expiry">
            {getExpiryLabel(market.resolved, market.expireSec)}:{" "}
            {formatDate(market.expireSec)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketsCard;
